"use client";
import { useRef } from "react";
import { z } from "zod";
import { useWebMcpTool } from "@/lib/webmcp/react";
import { buildPlanSchema, canvasEditSchema, reverseGoalSchema } from "@/lib/webmcp/planner";
import type { FunnelWizardState, AppLocale } from "@/lib/funnel-planner/schema";
import { computeReverseGoal, computeScenarioComparison, scenarioProjection, getTemplate } from "@/lib/funnel-planner/engine";
import { encodePlannerShareState } from "@/lib/funnel-planner/share-codec";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import type { PlannerAction } from "./FunnelPlannerWizard";

const empty = z.strictObject({});
type Props = {
  state: FunnelWizardState; ready: boolean; readOnly: boolean; locale: AppLocale;
  content: FunnelPlannerContent;
  dispatch: (action: PlannerAction) => void;
  flush: () => Promise<FunnelWizardState>;
  share: (signal: AbortSignal) => Promise<{ url: string; copied: boolean }>;
};
export function usePlannerWebMcp({ state, content, ready, readOnly, dispatch, flush, share }: Props) {
  const previewedShare = useRef<string | null>(null);
  const editable = ready && !readOnly;
  useWebMcpTool({ action: "build_funnel_plan", schema: buildPlanSchema, enabled: editable,
    execute: (input) => {
      if (input.templateId) {
        dispatch({ type: "SET_TEMPLATE", templateId: input.templateId });
        dispatch({ type: "SET_STAGES", stages: getTemplate(input.templateId).defaultStages.map((stage) => ({
          ...stage, name: String(content[`templates.${input.templateId}.stages.${stage.id}` as keyof FunnelPlannerContent] ?? stage.id),
          stageType: stage.stageType ?? "custom", publishedStatus: "draft" as const,
        })) });
      }
      if (input.audience) dispatch({ type: "SET_AUDIENCE", audience: input.audience });
      if (input.goal !== undefined) dispatch({ type: "SET_GOAL", goal: input.goal });
      if (input.projectionInputs) dispatch({ type: "SET_PROJECTION_INPUTS", inputs: input.projectionInputs });
      if (input.products) dispatch({ type: "SET_PRODUCTS", products: input.products });
      if (input.trafficChannels) dispatch({ type: "SET_TRAFFIC_CHANNELS", channels: input.trafficChannels });
      if (input.expenses) dispatch({ type: "SET_EXPENSES", expenses: input.expenses });
      if (input.regionalSettings) dispatch({ type: "SET_REGIONAL_SETTINGS", settings: input.regionalSettings });
      if (input.scenarios) dispatch({ type: "SET_SCENARIOS", scenarios: input.scenarios });
      if (input.step) dispatch({ type: "GO_TO_STEP", step: input.step as FunnelWizardState["step"] });
      return { status: "success", uiState: "draft_updated", data: { updatedFields: Object.keys(input) } };
    },
  });
  useWebMcpTool({ action: "compute_projection", schema: empty, enabled: ready,
    execute: async () => {
      const current = await flush();
      return { status: "success", data: scenarioProjection(current.stages, current.projectionInputs, 1, current.trafficChannels, current.graph) };
    },
  });
  useWebMcpTool({ action: "reverse_goal", schema: reverseGoalSchema, enabled: ready,
    execute: async ({ goalType, targetValue }) => {
      const current = await flush();
      return { status: "success", data: computeReverseGoal(goalType, targetValue, current.products, current.stages,
        current.trafficChannels, current.projectionInputs, 0, current.regionalSettings, current.graph, current.expenses) };
    },
  });
  useWebMcpTool({ action: "compare_scenarios", schema: empty, enabled: ready,
    execute: async () => {
      const current = await flush();
      return { status: "success", data: computeScenarioComparison(current.scenarios, current.stages, current.projectionInputs, current.trafficChannels, current.graph) };
    },
  });
  useWebMcpTool({ action: "edit_funnel_canvas", schema: canvasEditSchema, enabled: editable && Boolean(state.graph),
    execute: async ({ edits }, { signal }) => {
      const current = await flush();
      const graph = current.graph;
      if (signal.aborted) return { status: "cancelled" };
      if (!graph || edits.some((edit) => !graph.nodes.some((node) => node.id === edit.id)) || new Set(edits.map((edit) => edit.id)).size !== edits.length) return { status: "validation_error" };
      dispatch({ type: "SET_GRAPH", graph: { ...graph, nodes: graph.nodes.map((node) => {
        const edit = edits.find((item) => item.id === node.id);
        return edit ? { ...node, position: edit.position ?? node.position, data: { ...node.data,
          ...(edit.label !== undefined ? { label: edit.label } : {}),
          ...(edit.conversionRate !== undefined ? { conversionRate: edit.conversionRate } : {}),
        } } : node;
      }) } });
      return { status: "success", uiState: "canvas_updated", data: { updatedNodes: edits.map((edit) => edit.id) } };
    },
  });
  useWebMcpTool({ action: "create_share_link", schema: empty, enabled: editable && Boolean(state.templateId),
    preview: () => {
      previewedShare.current = encodePlannerShareState(state);
      // A human-readable recap, with the full contact-free payload fingerprinted
      // separately so edits during confirmation force a fresh review.
      return JSON.stringify({ effect: "create_public_link_and_copy", templateId: state.templateId,
        goal: state.goal, products: state.products.map(({ name }) => name), stages: state.stages.map(({ name }) => name),
        projectionInputs: state.projectionInputs, contactIncluded: false, publiclyAccessible: true }, null, 2);
    },
    execute: async (_, { signal }) => {
      const current = await flush();
      if (signal.aborted) return { status: "cancelled" };
      if (previewedShare.current !== encodePlannerShareState(current)) return { status: "unavailable", code: "plan_changed_review_again" };
      const result = await share(signal);
      return { status: "success", uiState: result.copied ? "link_created_and_copied" : "link_created_clipboard_failed", data: result };
    },
  });
}
